import { useState, useCallback, useMemo } from "react";
import { useForm, UseFormReturn, FieldValues, Path, DefaultValues, Mode, UseFormProps } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

export interface StepConfig<
  TFormData extends FieldValues,
  TFields extends readonly Path<TFormData>[] = readonly Path<TFormData>[],
> {
  id: string;
  schema: z.ZodSchema<Pick<TFormData, TFields[number]>>;
  fields: TFields;
  canContinue?: (data: Pick<TFormData, TFields[number]>) => boolean | Promise<boolean>;
  onStepComplete?: (data: Pick<TFormData, TFields[number]>) => void | Promise<void>;
}

export interface MultiStepFormConfig<
  TFormData extends FieldValues,
  TStepConfig extends StepConfig<TFormData, readonly Path<TFormData>[]> = StepConfig<
    TFormData,
    readonly Path<TFormData>[]
  >,
> {
  steps: TStepConfig[];
  defaultValues?: Partial<TFormData>;
  mode?: Mode;
  reValidateMode?: UseFormProps<TFormData>["reValidateMode"];
  fullSchema?: z.ZodSchema<TFormData>;
  onSubmit?: (data: TFormData) => void | Promise<void>;
}

export interface UseMultiStepFormReturn<
  TFormData extends FieldValues,
  TStepConfig extends StepConfig<TFormData, readonly Path<TFormData>[]> = StepConfig<
    TFormData,
    readonly Path<TFormData>[]
  >,
> {
  // form instance
  form: UseFormReturn<TFormData>;

  // step navigation
  steps: TStepConfig[];
  currentStep: number;
  currentStepConfig: TStepConfig;
  isFirstStep: boolean;
  isLastStep: boolean;
  totalSteps: number;

  // navigation methods
  nextStep: () => Promise<boolean>;
  previousStep: () => void;
  goToStep: (stepIndex: number) => void;

  // validation
  validateCurrentStep: () => Promise<boolean>;
  isCurrentStepValid: boolean;

  // data access
  getStepData: (stepIndex?: number) => Partial<TFormData>;
  getAllData: () => TFormData;

  // completion
  isFormComplete: boolean;
  submitForm: () => Promise<TFormData | null>;
}

export function useMultiStepForm<
  TFormData extends FieldValues,
  TStepConfig extends StepConfig<TFormData> = StepConfig<TFormData>,
>({
  steps,
  defaultValues,
  mode = "onTouched",
  reValidateMode = "onChange",
  fullSchema,
  onSubmit,
}: MultiStepFormConfig<TFormData, TStepConfig>): UseMultiStepFormReturn<TFormData, TStepConfig> {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  // Create a combined schema for the entire form
  // Use fullSchema if provided (to preserve refinements), otherwise build from steps
  const combinedSchema = useMemo(() => {
    if (fullSchema) {
      return fullSchema;
    }

    const schemaObject: Record<string, z.ZodSchema> = {};

    steps.forEach((step) => {
      // Handle different Zod schema types safely
      if (step.schema instanceof z.ZodObject) {
        const stepSchemaShape = step.schema.shape as Record<string, z.ZodSchema>;
        Object.assign(schemaObject, stepSchemaShape);
      }
    });

    return z.object(schemaObject);
  }, [steps, fullSchema]);

  // Initialize form with combined schema
  const form = useForm<TFormData>({
    mode,
    reValidateMode,
    resolver: zodResolver(combinedSchema),
    defaultValues: defaultValues as DefaultValues<TFormData>,
    shouldUnregister: false,
  });

  const currentStepConfig = steps[currentStep];

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const totalSteps = steps.length;

  // Validate current step
  const validateCurrentStep = useCallback(async (): Promise<boolean> => {
    const currentData = form.getValues();
    const stepData = {} as Pick<TFormData, (typeof currentStepConfig.fields)[number]>;

    // Extract only fields for current step
    currentStepConfig.fields.forEach((field) => {
      stepData[field] = currentData[field];
    });

    try {
      // Validate with step schema
      // await currentStepConfig.schema.parseAsync(stepData);

      // Trigger form validation for current step fields
      const isValid = await form.trigger(currentStepConfig.fields);

      if (isValid && currentStepConfig.canContinue) {
        return await currentStepConfig.canContinue(stepData);
      }

      return isValid;
    } catch {
      return false;
    }
  }, [form, currentStepConfig]);

  // Check if current step is valid
  const isCurrentStepValid = useMemo(() => {
    const formState = form.formState;
    return currentStepConfig.fields.every((field) => !formState.errors[field]);
  }, [form.formState, currentStepConfig.fields]);

  // Navigate to next step
  const nextStep = useCallback(async (): Promise<boolean> => {
    const isValid = await validateCurrentStep();

    if (!isValid) {
      return false;
    }

    const currentData = form.getValues();
    const stepData = {} as Pick<TFormData, (typeof currentStepConfig.fields)[number]>;

    currentStepConfig.fields.forEach((field) => {
      stepData[field] = currentData[field];
    });

    // Call onStepComplete if defined
    if (currentStepConfig.onStepComplete) {
      await currentStepConfig.onStepComplete(stepData);
    }

    // Mark step as completed
    setCompletedSteps((prev) => new Set([...prev, currentStep]));

    // If this is the last step and onSubmit is defined, call it with all form data
    if (isLastStep && onSubmit) {
      await onSubmit(currentData);
    }

    if (!isLastStep) {
      const nextStepIndex = currentStep + 1;
      setCurrentStep(nextStepIndex);
      // Scroll to the new step after DOM updates
      setTimeout(() => {
        const stepElement = document.querySelector(`[data-step-index="${String(nextStepIndex)}"]`);
        if (stepElement) {
          stepElement.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
        }
      }, 50);
    }

    return true;
  }, [validateCurrentStep, form, currentStepConfig, isLastStep, currentStep, onSubmit]);

  // Navigate to previous step
  const previousStep = useCallback(() => {
    if (!isFirstStep) {
      const prevStepIndex = currentStep - 1;
      setCurrentStep(prevStepIndex);
      // Scroll to the previous step after DOM updates
      setTimeout(() => {
        const stepElement = document.querySelector(`[data-step-index="${String(prevStepIndex)}"]`);
        if (stepElement) {
          stepElement.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
        }
      }, 50);
    }
  }, [isFirstStep, currentStep]);

  // Navigate to specific step
  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < steps.length) {
        setCurrentStep(stepIndex);
      }
    },
    [steps.length],
  );

  // Get data for specific step
  const getStepData = useCallback(
    (stepIndex?: number): Partial<TFormData> => {
      const targetStep = stepIndex !== undefined ? steps[stepIndex] : currentStepConfig;
      const allData = form.getValues();
      const stepData: Partial<TFormData> = {};

      targetStep.fields.forEach((field) => {
        stepData[field] = allData[field];
      });

      return stepData;
    },
    [form, steps, currentStepConfig],
  );

  // Get all form data
  const getAllData = useCallback((): TFormData => {
    return form.getValues();
  }, [form]);

  // Check if form is complete
  const isFormComplete = useMemo(() => {
    return completedSteps.size === steps.length;
  }, [completedSteps.size, steps.length]);

  // Submit entire form
  const submitForm = useCallback(async (): Promise<TFormData | null> => {
    // Validate all steps
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      const allData = form.getValues();
      const stepData: Partial<TFormData> = {};

      step.fields.forEach((field) => {
        stepData[field] = allData[field];
      });

      try {
        await step.schema.parseAsync(stepData);
      } catch {
        // If any step is invalid, go to that step and return null
        setCurrentStep(i);
        await form.trigger(step.fields);
        return null;
      }
    }

    // If all steps are valid, return the complete data
    const finalData = form.getValues();

    try {
      await combinedSchema.parseAsync(finalData);
      return finalData;
    } catch {
      return null;
    }
  }, [steps, form]);

  return {
    form,
    steps,
    currentStep,
    currentStepConfig,
    isFirstStep,
    isLastStep,
    totalSteps,
    nextStep,
    previousStep,
    goToStep,
    validateCurrentStep,
    isCurrentStepValid,
    getStepData,
    getAllData,
    isFormComplete,
    submitForm,
  };
}
